import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Main } from '@expo/html-elements';

import { getA11ySelector, useA11ySelector } from '@sg/a11y-helpers';
import {
  Alert,
  Container,
  FadeView,
  HStack,
  slugify,
  theme,
  useResponsive,
  useSafeFocusEffect,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
  useScrollspy,
} from '@sg/garnish';
import { MenuDietaryPreferences } from '@sg/shared-fe/features/menu';

import {
  MenuCategoriesGrid,
  MenuFloatingButtons,
  MenuOnlineOrderingUnavailable,
} from '@order/components/menu';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { telemetryLocationFromInput, useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { useBraze } from '../../../../context/Braze';
import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useCart, useCustomScreenTitle, useDeliveryOrderInFlight } from '../../../../hooks';
import { useReorderIfRequired } from '../../../../screens';
import { AppFooter } from '../../../AppFooter';
import { useDietaryPreferencesState } from '../../../DietaryRestrictions';
import {
  type useMenuContent,
  useMenuContentNavigation,
  type useProductDetailsContentData,
} from '../../hooks';
import {
  type MenuContentCategoryWithSlug,
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import {
  MenuContentDietaryPreferencesModal,
  useMenuContentDietaryPreferencesModal,
} from '../MenuContentDietaryPreferencesModal';
import { MenuContentHeader } from '../MenuContentHeader';
import { MenuContentNavigationHeader } from '../MenuContentNavigationHeader';
import { MenuContentProductCard } from '../MenuContentProductCard';
import { MenuContentTopDownModal } from '../MenuContentTopDownModal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentReady = (props: MenuContentReadyProps) => {
  const {
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    productDetailsContentData,
    categoriesBadges,
    caloriesOverrides,
  } = props;

  const { minWidth, match, currentBreakpoint } = useResponsive();
  const navigation = useSafeNavigation();
  const { formatMessage } = useIntl();
  const { lineItemsCount } = useCart();
  const { trackEvent } = useTelemetry();
  const { logCustomEvent } = useBraze();

  const locationHeaderA11ySelector = useA11ySelector('menu.location-header');

  const routeParams = useSafeRouteParams<{ referrer?: string; category?: string }>();

  const { referrer } = routeParams ?? {};

  // NOTE: Scrollspy updates the `category` route param after mount, so we
  // capture the initial value to detect whether the user deep-linked into a
  // category via URL anchor (e.g. `/menu/abc?category=wraps`).
  const hadInitialCategoryAnchorRef = useRef(Boolean(routeParams?.category));

  // ─────────────────────────────────────────────────────────────────────

  const {
    menu,
    hours,
    id: restaurantId,
    name: restaurantName,
    slug: restaurantSlug,
    isAcceptingOrders = true,
    notAcceptingOrdersReason,
  } = restaurantData;

  // NOTE: We need to hide empty categories and enrich with slugs for scroll spy anchors
  const filteredCategories = useMemo<MenuContentCategoryWithSlug[]>(
    () =>
      menu.categories
        .filter((category) => category.products.length > 0)
        .map((category) => ({ ...category, slug: slugify(category.name) })),
    [menu.categories],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isTopDownCategoriesModalEnabled = useFeatureFlag(
    'CELS-3094-menu-top-down-categories-enabled',
  );
  const isMacronutrientsEnabled = useFeatureFlag('CELS-4323-order-macronutrients-enabled');
  const isProductDetailsPageEnabled = useFeatureFlag(
    'CELS-4497-order-product-details-page-enabled',
  );

  const isOutpost = orderChannel === 'outpost';

  const shouldRenderNavigationHeader = match([false, true]);
  const shouldShowBagFloatingButton = currentBreakpoint.isXS && lineItemsCount > 0;

  const shouldRenderHoursMessage = isOutpost && typeof hours.formatted === 'string';

  const shouldRenderTopDownCategoriesModal =
    referrer !== 'bag' &&
    referrer !== 'reorder-confirm-location' &&
    isTopDownCategoriesModalEnabled &&
    currentBreakpoint.isXS &&
    !hadInitialCategoryAnchorRef.current;

  // ─── State ───────────────────────────────────────────────────────────

  const {
    hasOpenedDietaryPreferences,
    setHasOpenedDietaryPreferencesState,
    isResolvingDietaryPreferencesState,
  } = useDietaryPreferencesState();

  const {
    isDietaryPreferencesModalVisible,
    showDietaryPreferencesModal,
    hideDietaryPreferencesModal,
  } = useMenuContentDietaryPreferencesModal();

  const [isShowingMenuCategoriesModal, setIsShowingMenuCategoriesModal] = useState(
    shouldRenderTopDownCategoriesModal,
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const {
    generateCustomizationLink,
    generateCustomizationHref,
    setTargetParam,
    defaultActiveTargetId,
    changeLocation,
  } = useMenuContentNavigation({
    orderChannel,
    restaurantSlug,
    deliveryDetailsData,
  });

  const onContactUs = useCallback(() => {
    trackEvent({ name: 'menu.contact_us' });
  }, [trackEvent]);

  const hideMenuCategoriesModal = useCallback(() => {
    setIsShowingMenuCategoriesModal(false);
  }, []);

  const showTopDownModal = useCallback(() => {
    setIsShowingMenuCategoriesModal(true);
  }, []);

  const {
    activeTargetId,
    scrollViewRef,
    navScrollViewRef,
    scrollOffset,
    scrollTo,
    measureCoordinates,
    deregisterTarget,
    onScrollViewContentSizeChange,
    onScrollViewLayout,
  } = useScrollspy({
    defaultTargetId: defaultActiveTargetId ?? filteredCategories?.[0]?.slug,
    onActiveTargetIdChange: setTargetParam,
  });

  const onNavItemPress = useCallback(
    (categorySlug: string) => {
      const targetCategory = filteredCategories.find((category) => category.slug === categorySlug);

      if (!targetCategory) return;

      scrollTo(categorySlug);

      trackEvent({
        name: 'menu.jump_nav',
        payload: { id: restaurantSlug, tabName: targetCategory.name },
      });
    },
    [filteredCategories, restaurantSlug, scrollTo, trackEvent],
  );

  const onDietaryPreferencesModalClose = useCallback(() => {
    hideDietaryPreferencesModal();
    void setHasOpenedDietaryPreferencesState();
  }, [hideDietaryPreferencesModal, setHasOpenedDietaryPreferencesState]);

  // NOTE: In case of `undefined`, the default `goBack` action will be used.
  const customBackButtonHandler =
    shouldRenderTopDownCategoriesModal && !isShowingMenuCategoriesModal
      ? showTopDownModal
      : undefined;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const openBag = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/bag');
      return;
    }

    navigation?.navigate('Modal', { screen: 'Bag' });
  }, [isExpoRouterEnabled, router, navigation]);

  const trackMenuViewBrazeEvent = useCallback(() => {
    logCustomEvent('menu.view');
  }, [logCustomEvent]);

  useDeliveryOrderInFlight();
  useReorderIfRequired(restaurantSlug, deliveryDetailsData?.addressId);

  useSetLastInteractedMenuStore({
    restaurantId,
    restaurantName,
    restaurantSlug,
    addressId: deliveryDetailsData?.addressId,
    deliveryFee: deliveryDetailsData?.deliveryFee,
    vendor: deliveryDetailsData?.vendor,
    vendorRestaurantId: deliveryDetailsData?.vendorRestaurantId,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({
    name: 'menu.view',
    payload: { restaurant: telemetryLocationFromInput(restaurantData) },
  });

  /**
   * Only the smallest breakpoint uses a custom header, thus we need to
   * toggle the native header.
   */
  useLayoutEffect(() => {
    navigation?.setOptions({ headerShown: shouldRenderNavigationHeader });
  }, [navigation, shouldRenderNavigationHeader]);

  useCustomScreenTitle(
    formatMessage(messages.orderChannelAndLocationText, {
      order_channel: orderChannel,
      location: locationName,
    }),
  );

  useSafeFocusEffect(trackMenuViewBrazeEvent);

  // ─── Styles ──────────────────────────────────────────────────────────

  const infoMessagesContainerStyles = [
    styles.infoMessagesContainer,
    match([styles.infoMessagesContainerXS, styles.infoMessagesContainerSM]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.container}>
        {/* ─── Navigation Header ────────────────────────────────────────── */}

        <MenuContentNavigationHeader
          orderChannel={orderChannel}
          locationName={locationName}
          onBackBtnPressOverride={customBackButtonHandler}
        />

        {/* ─── Header ───────────────────────────────────────────────────── */}

        <MenuContentHeader
          menuGridScrollOffsetSV={scrollOffset}
          restaurantData={restaurantData}
          locationName={locationName}
          orderChannel={orderChannel}
          deliveryDetailsData={deliveryDetailsData}
          onContactUs={onContactUs}
          {...locationHeaderA11ySelector.props}
        >
          <MenuCategoriesGrid.Nav ref={navScrollViewRef} testID="menu-content.categories-nav">
            {filteredCategories.map((category) => (
              <MenuCategoriesGrid.NavItem
                key={category.id}
                category={category.name}
                targetId={category.slug}
                navScrollViewRef={navScrollViewRef}
                onPress={onNavItemPress}
                isActive={activeTargetId === category.slug}
              />
            ))}
          </MenuCategoriesGrid.Nav>
        </MenuContentHeader>

        {/* ─── Categories ───────────────────────────────────────────────── */}

        <MenuCategoriesGrid.Container
          ref={scrollViewRef}
          testID="menu-content.categories-container"
          onContentSizeChange={onScrollViewContentSizeChange}
          onLayout={onScrollViewLayout}
        >
          <Main style={styles.contentContainer}>
            <Container>
              {filteredCategories.map((category, index) => {
                const isFirstCategory = index === 0;
                const isCustomCategory = category.isCustom;

                const shouldRenderStoreHoursMessage = isFirstCategory && shouldRenderHoursMessage; // prettier-ignore
                const shouldRenderDietaryPreferencesToggle = isFirstCategory && !isResolvingDietaryPreferencesState && !hasOpenedDietaryPreferences; // prettier-ignore
                const shouldRenderInfoMessages = shouldRenderStoreHoursMessage || shouldRenderDietaryPreferencesToggle; // prettier-ignore

                const a11ySelector = getA11ySelector('menu.categories-grid.category.container', {
                  group: category.name.toLowerCase(),
                  index,
                });

                const maybeCategoryBadge = categoriesBadges[category.id];

                // ─── Custom Category ───────────────────────────────────────────

                if (isCustomCategory) {
                  const customProducts = category.products.filter((product) => product.isCustom);

                  if (customProducts.length === 0) return null;

                  const hasMultipleProducts = customProducts.length > 1;
                  const itemsPerRow = hasMultipleProducts ? match([1, 1, 2]) : 1;

                  return (
                    <MenuCategoriesGrid.Category
                      key={category.id}
                      targetId={category.slug}
                      // @ts-expect-error - scrollViewRef type mismatch
                      scrollViewRef={scrollViewRef}
                      measureCoordinates={measureCoordinates}
                      deregisterTarget={deregisterTarget}
                      {...a11ySelector.props}
                    >
                      <HStack itemsPerRow={itemsPerRow} gap={theme.spacing['4']}>
                        {customProducts.map((product) => (
                          <MenuCategoriesGrid.CustomProductCard
                            key={product.id}
                            product={product}
                            generateLink={generateCustomizationLink}
                            generateHref={generateCustomizationHref}
                          />
                        ))}
                      </HStack>
                    </MenuCategoriesGrid.Category>
                  );
                }

                // ─── Standard Category ─────────────────────────────────────────

                return (
                  <MenuCategoriesGrid.Category
                    key={category.id}
                    targetId={category.slug}
                    measureCoordinates={measureCoordinates}
                    deregisterTarget={deregisterTarget}
                    {...a11ySelector.props}
                  >
                    {/* NOTE: We use the first category to render extra components */}

                    {shouldRenderInfoMessages ? (
                      <View style={infoMessagesContainerStyles}>
                        {shouldRenderStoreHoursMessage ? (
                          <Alert.Container palette="info">
                            <Alert.Text>{hours.formatted}</Alert.Text>
                          </Alert.Container>
                        ) : null}

                        {shouldRenderDietaryPreferencesToggle ? (
                          <MenuDietaryPreferences.Toggle onPress={showDietaryPreferencesModal} />
                        ) : null}
                      </View>
                    ) : null}

                    <MenuCategoriesGrid.CategoryHeading badge={maybeCategoryBadge}>
                      {category.name}
                    </MenuCategoriesGrid.CategoryHeading>

                    <MenuCategoriesGrid.ProductsStack>
                      {category.products.map((product) => {
                        const { id } = product;

                        const productDetailsContentDataItem = productDetailsContentData?.get(product.baseProduct.slug); // prettier-ignore
                        const productDetailsData = productDetailsContentDataItem?.fields; // prettier-ignore
                        const hasProductDetails = Boolean(productDetailsData?.isEnabled); // prettier-ignore

                        const cardHoverImage = productDetailsData?.cardHoverImage; // prettier-ignore
                        const cardHoverStaticImage = productDetailsData?.cardHoverStaticImage; // prettier-ignore
                        const carousel = productDetailsData?.carousel;

                        const hoverImageUrl = cardHoverImage?.fields?.file?.url;
                        const hoverStaticImageUrl = cardHoverStaticImage?.fields?.file?.url;
                        const hoverImageAlt = cardHoverImage?.fields?.title;
                        const caloriesOverrideEntry = caloriesOverrides?.[product.baseProduct.slug];

                        return (
                          <MenuContentProductCard
                            key={id}
                            product={product}
                            hasProductDetails={hasProductDetails}
                            restaurantSlug={restaurantSlug}
                            deliveryDetails={deliveryDetailsData}
                            generateLink={generateCustomizationLink}
                            generateHref={generateCustomizationHref}
                            imageFetchingPriority={isShowingMenuCategoriesModal ? 'low' : 'normal'} // prettier-ignore
                            shouldShowNutritionData={isMacronutrientsEnabled}
                            shouldShowNutritionLink={isProductDetailsPageEnabled} // prettier-ignore
                            hoverImageUrl={hoverImageUrl}
                            hoverStaticImageUrl={hoverStaticImageUrl}
                            hoverImageAlt={hoverImageAlt}
                            carousel={carousel}
                            calorieOverride={caloriesOverrideEntry?.caloriesOverride}
                            calorieOverrideText={caloriesOverrideEntry?.caloriesOverrideText}
                          />
                        );
                      })}
                    </MenuCategoriesGrid.ProductsStack>
                  </MenuCategoriesGrid.Category>
                );
              })}
            </Container>
          </Main>

          {minWidth.isSM ? <AppFooter /> : null}
        </MenuCategoriesGrid.Container>
      </View>

      {/* ─── Modals ─────────────────────────────────────────────────────── */}

      <MenuContentDietaryPreferencesModal
        isVisible={isDietaryPreferencesModalVisible}
        onClose={onDietaryPreferencesModalClose}
      />

      {isAcceptingOrders ? null : (
        <MenuOnlineOrderingUnavailable.Container>
          <MenuOnlineOrderingUnavailable.Dialog>
            <MenuOnlineOrderingUnavailable.Message message={notAcceptingOrdersReason} />
            <MenuOnlineOrderingUnavailable.Button onPress={changeLocation} />
          </MenuOnlineOrderingUnavailable.Dialog>
        </MenuOnlineOrderingUnavailable.Container>
      )}

      {shouldRenderTopDownCategoriesModal ? (
        <MenuContentTopDownModal
          orderChannel={orderChannel}
          locationName={locationName}
          categories={filteredCategories}
          categoriesBadges={categoriesBadges}
          isVisible={isShowingMenuCategoriesModal}
          hideModal={hideMenuCategoriesModal}
          onCategoryPress={scrollTo}
        />
      ) : null}

      {/* ─── Floating Buttons ───────────────────────────────────────────── */}

      <FadeView show={shouldShowBagFloatingButton}>
        <MenuFloatingButtons.Bag lineItemsCount={lineItemsCount} onPress={openBag} />
      </FadeView>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryEtaText: {
    defaultMessage: 'Delivers in {eta} minutes',
    description: 'Menu | Header | Delivery ETA text',
  },
  orderChannelAndLocationText: {
    defaultMessage: `{order_channel, select,
      pickup {Pickup from {location}}
      delivery {Delivery to {location}}
      outpost {Outpost at {location}}
      other {}
    }`,
    description: 'Menu | Navigation Header | Order channel and location text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing['16'],

    // required for web `<Main>` component

    display: 'flex',
    flexDirection: 'column',
  },

  infoMessagesContainer: {
    alignItems: 'flex-start',
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['8'],
  },
  infoMessagesContainerXS: {
    marginTop: -theme.spacing['4'],
  },
  infoMessagesContainerSM: {
    marginTop: -theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentReadyProps = {
  restaurantData: MenuContentRestaurantData;
  productDetailsContentData: ProductDetailsContentData;
  categoriesBadges: CategoriesBadges;
  caloriesOverrides: CaloriesOverrides;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
  locationName: string;
};

type ProductDetailsContentData = ReturnType<
  typeof useProductDetailsContentData
>['productDetailsContentData'];

type CategoriesBadges = ReturnType<typeof useMenuContent>['mappedContent']['categoriesBadges'];
type CaloriesOverrides = ReturnType<typeof useMenuContent>['mappedContent']['caloriesOverrides'];
